import React, { createContext, useEffect, useState, useCallback } from 'react'
import { ProductService } from '../services/product.service'
import { ProductFeature } from '../types/product-feature'
import { logger } from 'app/components'
import { productFeaturesObject } from '../product-features'

export interface ProductFeatureContextType {
  productFeatures: string
  isLoading: boolean
  loadProductFeatures: () => Promise<void>
  hasProductFeature: (feature: ProductFeature) => boolean
}

const defaultContextValue: ProductFeatureContextType = {
  productFeatures: '',
  isLoading: false,
  loadProductFeatures: async () => {},
  hasProductFeature: () => false,
}

export const productFeatureContext = createContext<ProductFeatureContextType>(defaultContextValue)

export const WithProductFeatureContext = ({ children }: { children: JSX.Element | JSX.Element[] }): JSX.Element => {
  const [productFeatures, setProductFeatures] = useState<string>('')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')

  const loadProductFeatures = useCallback(async () => {
    setIsLoading(true)
    setError('')
    try {
      const response = await ProductService.getProductFeatures()

      if (response.productFeatures) {
        logger.debug('Loaded product features:', response.productFeatures)
        const features = response.productFeatures ?? ''
        setProductFeatures(features)
        productFeaturesObject.setFeatures(features)
      }
    } catch (error: any) {
      setError(error.message)
      logger.warn('Product features could not be loaded:', error)
      setProductFeatures('')
      productFeaturesObject.setFeatures('')
    } finally {
      setIsLoading(false)
    }
  }, [])

  const hasProductFeature = useCallback(
    (feature: ProductFeature): boolean => {
      return productFeatures.includes(feature)
    },
    [productFeatures]
  )

  useEffect(() => {
    loadProductFeatures()
  }, [loadProductFeatures])

  const contextValue: ProductFeatureContextType = {
    productFeatures,
    isLoading,
    loadProductFeatures,
    hasProductFeature,
  }

  return <productFeatureContext.Provider value={contextValue}>{children}</productFeatureContext.Provider>
}
